'use client';

import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  NAME_VARIANT_ROLE_LABELS,
  RECONCILIATION_BASIS_LABELS,
  useCreateNameVariant,
  useDeleteNameVariant,
  useIdentityVariants,
  useUpdateNameVariant,
  type IdentityVariantOwnerType,
  type IdentityVariantsResponse,
  type NameVariant
} from '@/lib/services/identity-variants';
import { IdentityVariantRow } from './identity-variant-row';
import { VariantSubsection } from './variant-subsection';
import { NameVariantForm } from './name-variant-form';

/**
 * "Other names" — the other names a person is known by (LF-5566 PROV-21),
 * folded under the host form's name fields in LF-5714.
 *
 * Names **supplement** the base record, they never replace it — conflating the
 * two is what made the single free-text `alias` field unable to express a
 * roled, provenanced set of names. So this sits under the name fields as a
 * subsection rather than inside them.
 *
 * Why it exists at all: variants already flow into share-registry letters and
 * court-form alias recitals, but were previously write-only from AI extraction,
 * so a wrong AI-created name reached an institution and a court with no human
 * able to see or correct it. Every row here is editable and removable, and
 * carries its own provenance.
 *
 * Rows persist immediately through their own endpoints; they are **not** part of
 * the surrounding form's submit. That keeps a child-row edit from being lost
 * when the parent form is cancelled, and matches how the data is stored.
 */

interface OtherNamesSubsectionProps {
  ownerType: IdentityVariantOwnerType;
  /** Undefined while the owner is unsaved — the subsection renders a hint instead. */
  ownerId: number | undefined;
  /** Storybook / test seam — bypasses the query so the subsection renders given data. */
  data?: IdentityVariantsResponse;
}

export function OtherNamesSubsection({
  ownerType,
  ownerId,
  data: dataOverride
}: OtherNamesSubsectionProps) {
  const owner = { ownerType, ownerId };
  /** The row being edited, `'new'` while adding, or null. */
  const [editing, setEditing] = useState<NameVariant | 'new' | null>(null);
  const close = () => setEditing(null);

  const query = useIdentityVariants(owner, {
    enabled: !dataOverride && !!ownerId
  });
  const data = dataOverride ?? query.data;

  const create = useCreateNameVariant(owner);
  const update = useUpdateNameVariant(owner);
  const remove = useDeleteNameVariant(owner);

  /*
   * Primary rows are excluded. `isPrimary` on a name variant has exactly one
   * consumer — `deceased-identity-variant.service.ts` filters it out of
   * `akaNameVariants` because the primary row *is* the canonical name, already
   * rendered from `matters.decedent_*` / the contact's own name fields. So a
   * primary variant duplicates the fields directly above this subsection and is
   * by definition not an "other" name; listing it here, badged PRIMARY,
   * contradicted the heading. This list is now exactly the aka set the letters
   * use.
   */
  const variants = (data?.nameVariants ?? []).filter(
    (variant) => !variant.isPrimary
  );

  if (!ownerId) {
    return (
      <p className="text-muted-foreground text-xs">
        Save this {ownerType === 'matter' ? 'matter' : 'contact'} first to
        record other names.
      </p>
    );
  }

  const isBusy = create.isPending || update.isPending;

  if (query.isLoading && !dataOverride) {
    return <Skeleton className="h-4 w-32" />;
  }

  return (
    <VariantSubsection
      label="Other names"
      addLabel="Add name"
      count={variants.length}
      onAdd={() => setEditing('new')}
      disabled={isBusy}
      isEmpty={variants.length === 0 && !editing}
      emptyHint="No other names recorded."
    >
      <>
        {variants.map((variant) =>
          editing !== 'new' && editing?.id === variant.id ? (
            <NameVariantForm
              key={`edit-${variant.id}`}
              variant={variant}
              isSubmitting={update.isPending}
              onCancel={close}
              onSubmit={(payload) =>
                update.mutateAsync({ id: variant.id, data: payload })
              }
            />
          ) : (
            <IdentityVariantRow
              key={variant.id}
              value={variant.value}
              meta={[
                NAME_VARIANT_ROLE_LABELS[variant.role],
                !variant.isCurrent && 'no longer used',
                variant.reconciliationBasis &&
                  RECONCILIATION_BASIS_LABELS[variant.reconciliationBasis]
              ]}
              provenance={variant.provenance}
              entityLabel="name"
              disabled={isBusy}
              onEdit={() => setEditing(variant)}
              onDelete={async () => {
                await remove.mutateAsync(variant.id);
              }}
            />
          )
        )}
        {editing === 'new' && (
          <NameVariantForm
            isSubmitting={create.isPending}
            onCancel={close}
            onSubmit={(payload) => create.mutateAsync(payload)}
          />
        )}
      </>
    </VariantSubsection>
  );
}
